import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { Share2, List, ArrowUpRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { cn } from '../../utils/cn';
import { StockCategoryFilter, CategoryHelpers } from '../../types/stockCategories';
import { StockCombobox } from '../ui/modals/SelectionModal';
import { normalizeStockOption } from '../../utils/stockOptionFormatting';
import { ErrorBoundary } from '../ErrorBoundary';

interface GuessInputProps {
  onSubmit: (guess: string) => void;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  className?: string;
  suggestions?: string[];
  category?: StockCategoryFilter;
  showCategoryContext?: boolean;
  mode?: 'submit' | 'results';
  onResults?: () => void;
  onInvalidGuess?: (error: string) => void;
  onSearchSuggestions?: (query: string) => Promise<string[]>;
  suggestionsLoading?: boolean;
  suggestionsError?: string | null;
  onCopyResults?: () => void;
}

export interface GuessInputRef {
  triggerErrorFlash: () => void;
}

export const GuessInput = forwardRef<GuessInputRef, GuessInputProps>(({
  onSubmit,
  disabled = false,
  placeholder,
  className,
  suggestions = [],
  category,
  mode = 'submit',
  onResults,
  onCopyResults
}, ref) => {
  // Generate category-aware placeholder if not provided
  const defaultPlaceholder = 'Search by company or ticker (e.g. Apple / $AAPL)';
  const finalPlaceholder = placeholder || defaultPlaceholder;

  // Component state
  const [selectedValue, setSelectedValue] = useState('');
  const [isFlashingError, setIsFlashingError] = useState(false);
  const [isShakingCard, setIsShakingCard] = useState(false);
  const [selectAllTrigger, setSelectAllTrigger] = useState(0);

  // Timeout refs for cleanup on rapid re-triggers
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const shakeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Expose triggerErrorFlash method to parent via ref
  useImperativeHandle(ref, () => ({
    triggerErrorFlash: () => {
      // Clear previous timeouts so rapid guesses restart cleanly
      clearTimeout(flashTimeoutRef.current);
      clearTimeout(shakeTimeoutRef.current);

      setIsFlashingError(false);
      setIsShakingCard(false);

      // Force a re-render before re-enabling so the animation restarts
      requestAnimationFrame(() => {
        setIsFlashingError(true);
        setIsShakingCard(true);

        flashTimeoutRef.current = setTimeout(() => setIsFlashingError(false), 300);
        shakeTimeoutRef.current = setTimeout(() => setIsShakingCard(false), 500);
      });
    }
  }), []);

  // Mode and disabled state
  const isResultsMode = mode === 'results';
  const isInputDisabled = disabled || isResultsMode;

  // Get category config for heading
  const categoryConfig = category ? CategoryHelpers.getConfig(category) : null;

  // Reset selected value when switching to results mode
  useEffect(() => {
    if (isResultsMode) {
      setSelectedValue('');
    }
  }, [isResultsMode]);

  // Handle form submission
  const handleSubmit = (guess?: string) => {
    const submittedGuess = guess !== undefined ? guess.trim() : selectedValue.trim();
    if (!submittedGuess || isInputDisabled || isResultsMode) {
      return;
    }

    onSubmit(submittedGuess);
    setSelectedValue('');
  };

  // Handle combobox selection
  const handleSelect = (option: string) => {
    try {
      if (!option || typeof option !== 'string') {
        console.warn('handleSelect received invalid option:', option);
        return;
      }
      const normalized = normalizeStockOption(option);
      setSelectedValue(normalized.value);
    } catch (error) {
      console.error('Error handling selection:', error);
    }
  };

  // Handle combobox submission (Enter key)
  const handleComboboxSubmit = (option: string) => {
    try {
      if (!option || typeof option !== 'string') {
        return;
      }
      const normalized = normalizeStockOption(option);
      handleSubmit(normalized.value);
    } catch (error) {
      console.error('Error handling combobox submit:', error);
    }
  };

  return (
    <div className={cn("relative w-full", categoryConfig && "mt-4", className)}>
      {categoryConfig && (
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex items-center justify-center"
          style={{ clipPath: 'polygon(6% 0%, 94% 0%, 100% 50%, 94% 100%, 6% 100%, 0% 50%)' }}
        >
          <div className="bg-black dark:bg-white p-[1.5px]" style={{ clipPath: 'polygon(6% 0%, 94% 0%, 100% 50%, 94% 100%, 6% 100%, 0% 50%)' }}>
            <div
              className="flex items-center bg-white dark:bg-card py-1 px-6"
              style={{ clipPath: 'polygon(6% 0%, 94% 0%, 100% 50%, 94% 100%, 6% 100%, 0% 50%)' }}
            >
              <span className="font-bold font-serif text-foreground whitespace-nowrap text-[clamp(0.875rem,1.8vw,1.125rem)]">
                Guess the stock
              </span>
            </div>
          </div>
        </div>
      )}
      <Card className={cn(
        "p-4 pt-6 bg-white dark:bg-card border border-black dark:border-white shadow-[0_4px_20px_rgba(0,0,0,0.25)] dark:shadow-[0_4px_20px_rgba(255,255,255,0.15)]",
        isFlashingError && "animate-error-flash",
        isShakingCard && "animate-shake"
      )}>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <ErrorBoundary
              isolate={true}
              fallback={
                <div className="flex-1 p-3 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded">
                  Unable to load stock options. Please try again.
                </div>
              }
            >
              <div className="flex-1">
                {isResultsMode ? (
                  // Results mode: Show disabled state
                  <div className="relative">
                    <div className="w-full rounded-xl border border-border bg-background/60 pl-4 pr-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                      Game completed
                    </div>
                  </div>
                ) : (
                  // Submit mode: Show combobox
                  <StockCombobox
                    options={suggestions}
                    onSelect={handleSelect}
                    onSubmit={handleComboboxSubmit}
                    category={category}
                    placeholder={finalPlaceholder}
                    disabled={isInputDisabled}
                    selectAllTrigger={selectAllTrigger}
                  />
                )}
              </div>
            </ErrorBoundary>

            <Button
              onClick={() => {
                if (isResultsMode) {
                  onResults?.();
                  return;
                }
                handleSubmit();
              }}
              disabled={isResultsMode ? !onResults : isInputDisabled}
              className={cn(
                "h-auto px-6 py-3 text-sm",
                isResultsMode
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-black hover:bg-black/90 text-white dark:bg-white dark:hover:bg-white/90 dark:text-black"
              )}
            >
              {isResultsMode ? 'Results' : 'Submit'}
            </Button>
          </div>

          {/* Options Bar - Conditionally renders based on mode */}
          {isResultsMode ? (
            <button
              onClick={onCopyResults}
              disabled={!onCopyResults}
              className={cn(
                "w-full py-2 px-4 rounded-lg text-black text-sm font-medium",
                "bg-green-300",
                "hover:bg-green-400",
                "transition-colors duration-200",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                "cursor-pointer",
                "flex items-center justify-center gap-2"
              )}
              aria-label="Share results"
            >
              <Share2 className="w-4 h-4" />
              Share Results
            </button>
          ) : (
            <button
              type="button"
              disabled={isInputDisabled}
              onClick={() => setSelectAllTrigger(prev => prev + 1)}
              className={cn(
                "w-full flex items-center justify-between gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold",
                "bg-blue-600 text-white",
                "dark:bg-blue-500 dark:text-white",
                "hover:bg-blue-700 dark:hover:bg-blue-600 hover:-translate-y-0.5 transition-all duration-150",
                "disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              )}
            >
              <span className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-white/20">
                  <List className="h-4 w-4" />
                </span>
                Browse all stocks
              </span>
              <ArrowUpRight className="h-4 w-4 opacity-80" />
            </button>
          )}
          {/* Helper text */}
          <p className="text-xs text-center italic font-bold leading-tight bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(to right, #14b8a6, #10b981, #eab308, #14b8a6, #10b981, #eab308)' }}>
            {isResultsMode ? 'All information revealed!' : 'Each incorrect guess reveals new information'}
          </p>
        </div>
      </Card>
    </div>
  );
});

GuessInput.displayName = 'GuessInput';

export default GuessInput;
